import { useMemo, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/shared/StatusBadge";
import {
  useImportProspects,
  type ImportProspectsResult,
  type ProspectField,
} from "@/hooks/useProspects";
import {
  importLimitErrors,
  inferFieldType,
  MAX_IMPORT_ROWS,
  PROSPECT_CORE_FIELDS,
  PROSPECT_FIELD_TYPES,
  suggestColumnPlan,
  type ColumnPlan,
  type ImportTarget,
  type ProspectFieldType,
  slugifyKey,
  uniqueKey,
} from "@/lib/prospectFields";
import { FIELD_TYPE_META } from "@/lib/prospectDisplay";
import { readSpreadsheet, SPREADSHEET_ACCEPT } from "@/lib/readSpreadsheet";
import { parseDelimitedText, type SheetRecords } from "@/lib/sheetImport";

/**
 * Importação em massa de leads frios para uma lista: arquivo (CSV/TSV/XLSX)
 * ou texto colado do Google Sheets/Excel → mapeamento das colunas (campo
 * básico, coluna existente, coluna nova com tipo inferido ou ignorar) →
 * resultado. Reimportar com ID externo atualiza sem duplicar.
 */
export interface ImportProspectsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listId: string;
  listName: string;
  fields: ProspectField[];
}

type Step = "source" | "map" | "done";
const MAX_FILE_ROWS = MAX_IMPORT_ROWS * 10;

function targetValue(target: ImportTarget): string {
  if (target.kind === "core") return `core:${target.key}`;
  if (target.kind === "field") return target.isNew ? "new" : `field:${target.key}`;
  return "ignore";
}

export function ImportProspectsDialog({
  open,
  onOpenChange,
  listId,
  listName,
  fields,
}: ImportProspectsDialogProps) {
  const [step, setStep] = useState<Step>("source");
  const [sheet, setSheet] = useState<SheetRecords | null>(null);
  const [fileName, setFileName] = useState("");
  const [pasted, setPasted] = useState("");
  const [plan, setPlan] = useState<ColumnPlan[]>([]);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [readError, setReadError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [result, setResult] = useState<ImportProspectsResult | null>(null);
  const importProspects = useImportProspects();

  const reset = () => {
    setStep("source");
    setSheet(null);
    setFileName("");
    setPasted("");
    setPlan([]);
    setResult(null);
    setReadError(null);
    importProspects.reset();
  };

  const close = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const loadSheet = (data: SheetRecords, name: string) => {
    if (data.records.length === 0) {
      setReadError("Nenhuma linha encontrada. A primeira linha deve ser o cabeçalho.");
      return;
    }
    if (data.records.length > MAX_FILE_ROWS) {
      setReadError(`Máximo de ${MAX_FILE_ROWS} linhas por importação.`);
      return;
    }
    setReadError(null);
    setSheet(data);
    setFileName(name);
    setPlan(suggestColumnPlan(data.headers, data.records, fields));
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setReading(true);
    try {
      loadSheet(await readSpreadsheet(file), file.name);
    } catch {
      setReadError("Não foi possível ler o arquivo. Use CSV, TSV ou XLSX.");
    } finally {
      setReading(false);
    }
  };

  const samples = useMemo(() => {
    const out: Record<string, string> = {};
    for (const header of sheet?.headers ?? []) {
      const hit = sheet?.records.find((r) => String(r[header] ?? "").trim() !== "");
      out[header] = hit ? String(hit[header]).slice(0, 80) : "";
    }
    return out;
  }, [sheet]);

  const limitErrors = sheet ? importLimitErrors(sheet.headers.length, plan, fields.length) : [];
  const counts = {
    core: plan.filter((p) => p.target.kind === "core").length,
    created: plan.filter((p) => p.target.kind === "field" && p.target.isNew).length,
    ignored: plan.filter((p) => p.target.kind === "ignore").length,
  };
  const hasName = plan.some((p) => p.target.kind === "core" && p.target.key === "name");
  const hasExternalId = plan.some(
    (p) => p.target.kind === "core" && p.target.key === "external_id",
  );

  const changeTarget = (index: number, value: string) => {
    setPlan((cur) =>
      cur.map((p, i) => {
        if (i !== index) {
          // Um campo básico só pode receber uma coluna.
          if (value.startsWith("core:") && targetValue(p.target) === value) {
            return { ...p, target: { kind: "ignore" } };
          }
          return p;
        }
        if (value === "ignore") return { ...p, target: { kind: "ignore" } };
        if (value.startsWith("core:")) {
          return { ...p, target: { kind: "core", key: value.slice(5) as never } };
        }
        if (value.startsWith("field:")) {
          const field = fields.find((f) => f.key === value.slice(6))!;
          return {
            ...p,
            target: {
              kind: "field",
              key: field.key,
              label: field.label,
              type: field.type,
              isNew: false,
            },
          };
        }
        const taken = new Set([
          ...fields.map((f) => f.key),
          ...cur.flatMap((x) => (x.target.kind === "field" ? [x.target.key] : [])),
        ]);
        return {
          ...p,
          target: {
            kind: "field",
            key: uniqueKey(slugifyKey(p.source), taken),
            label: p.source.trim().slice(0, 80) || "Coluna",
            type: inferFieldType((sheet?.records ?? []).slice(0, 200).map((r) => r[p.source])),
            isNew: true,
          },
        };
      }),
    );
  };

  const changeNewType = (index: number, type: ProspectFieldType) =>
    setPlan((cur) =>
      cur.map((p, i) =>
        i === index && p.target.kind === "field" ? { ...p, target: { ...p.target, type } } : p,
      ),
    );

  const runImport = async () => {
    if (!sheet) return;
    const res = await importProspects.mutateAsync({
      listId,
      fields,
      headers: sheet.headers,
      records: sheet.records,
      plan,
      updateExisting,
    });
    setResult(res);
    setStep("done");
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[92vh] max-w-[880px] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar para “{listName}”</DialogTitle>
          <DialogDescription>
            {step === "source" && "Passo 1 de 3 — arquivo CSV/XLSX ou texto colado da planilha."}
            {step === "map" && "Passo 2 de 3 — para onde vai cada coluna."}
            {step === "done" && "Importação concluída."}
          </DialogDescription>
        </DialogHeader>

        {step === "source" && (
          <div className="flex flex-col gap-4">
            <Tabs defaultValue="file">
              <TabsList>
                <TabsTrigger value="file">Arquivo</TabsTrigger>
                <TabsTrigger value="paste">Colar da planilha</TabsTrigger>
              </TabsList>
              <TabsContent value="file">
                <label
                  htmlFor="prospect-import-file"
                  className="flex cursor-pointer flex-col items-center gap-2 rounded-[14px] border-2 border-dashed border-border px-4 py-8 text-center hover:bg-muted/40"
                >
                  {reading ? (
                    <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
                  ) : (
                    <Upload className="h-6 w-6 text-primary" aria-hidden="true" />
                  )}
                  <span className="text-sm font-semibold">Escolher arquivo</span>
                  <span className="text-xs text-muted-foreground">
                    CSV, TSV ou XLSX · primeira linha = cabeçalho
                  </span>
                </label>
                <input
                  id="prospect-import-file"
                  type="file"
                  accept={SPREADSHEET_ACCEPT}
                  className="sr-only"
                  onChange={(e) => onFile(e.target.files?.[0])}
                />
              </TabsContent>
              <TabsContent value="paste" className="flex flex-col gap-2">
                <Label htmlFor="prospect-import-paste">Cole as células (com o cabeçalho)</Label>
                <Textarea
                  id="prospect-import-paste"
                  rows={8}
                  value={pasted}
                  onChange={(e) => setPasted(e.target.value)}
                  placeholder={
                    "Empresa\tCidade\tTelefone\nRestaurante Diadorim\tTrês Marias\t(38) 3754-1744"
                  }
                  className="font-mono text-xs"
                />
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!pasted.trim()}
                    onClick={() => loadSheet(parseDelimitedText(pasted), "texto colado")}
                  >
                    Ler dados colados
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
            {readError && (
              <p role="alert" className="text-sm font-semibold text-bad">
                {readError}
              </p>
            )}
            {sheet && (
              <div className="flex items-center justify-between gap-3 rounded-[10px] bg-accent px-3 py-2.5 text-sm">
                <span>
                  <strong>{fileName}</strong> · {sheet.records.length} linhas ·{" "}
                  {sheet.headers.length} colunas
                </span>
                <Button type="button" onClick={() => setStep("map")}>
                  Mapear colunas
                </Button>
              </div>
            )}
          </div>
        )}

        {step === "map" && sheet && (
          <div className="flex flex-col gap-3">
            <p className="text-[12.5px] text-muted-foreground">
              {counts.core} campos básicos · {counts.created} colunas novas · {counts.ignored}{" "}
              ignoradas
            </p>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full min-w-[720px] text-[13px]">
                <thead>
                  <tr className="bg-muted text-left text-[11px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                    <th className="px-3.5 py-2.5">Coluna da planilha</th>
                    <th className="px-3 py-2.5">Exemplo</th>
                    <th className="px-3 py-2.5">Vai para</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.map((p, index) => {
                    const selectId = `map-target-${index}`;
                    return (
                      <tr key={p.source} className="border-t border-border">
                        <td className="px-3.5 py-2 font-semibold">
                          <label htmlFor={selectId}>{p.source}</label>
                        </td>
                        <td className="max-w-[240px] truncate px-3 py-2 text-muted-foreground">
                          {samples[p.source] || "—"}
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <Select
                              value={targetValue(p.target)}
                              onValueChange={(v) => changeTarget(index, v)}
                            >
                              <SelectTrigger id={selectId} className="h-8 w-[220px] text-[13px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="ignore">Não importar</SelectItem>
                                <SelectItem value="new">Nova coluna</SelectItem>
                                <SelectGroup>
                                  <SelectLabel>Campos básicos</SelectLabel>
                                  {PROSPECT_CORE_FIELDS.map((f) => (
                                    <SelectItem key={f.key} value={`core:${f.key}`}>
                                      {f.label}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                                {fields.length > 0 && (
                                  <SelectGroup>
                                    <SelectLabel>Colunas da lista</SelectLabel>
                                    {fields.map((f) => (
                                      <SelectItem key={f.key} value={`field:${f.key}`}>
                                        {f.label}
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                )}
                              </SelectContent>
                            </Select>
                            {p.target.kind === "field" && p.target.isNew && (
                              <Select
                                value={p.target.type}
                                onValueChange={(v) => changeNewType(index, v as ProspectFieldType)}
                              >
                                <SelectTrigger
                                  aria-label={`Tipo da nova coluna ${p.source}`}
                                  className="h-8 w-[170px] text-[13px]"
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {PROSPECT_FIELD_TYPES.map((t) => (
                                    <SelectItem key={t} value={t}>
                                      {FIELD_TYPE_META[t].label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {hasExternalId && (
              <div className="flex items-start gap-2 text-sm">
                <Checkbox
                  id="import-update-existing"
                  checked={updateExisting}
                  onCheckedChange={(v) => setUpdateExisting(v === true)}
                />
                <Label htmlFor="import-update-existing" className="font-normal leading-snug">
                  Atualizar quem já existe (mesmo ID externo) em vez de pular
                </Label>
              </div>
            )}
            {!hasExternalId && (
              <p className="text-[12.5px] text-muted-foreground">
                Sem coluna de ID externo: reimportar este arquivo duplica as linhas.
              </p>
            )}
            {!hasName && (
              <p role="alert" className="text-sm font-semibold text-bad">
                Escolha qual coluna vai para “Nome”.
              </p>
            )}
            {limitErrors.map((e) => (
              <p key={e} role="alert" className="text-sm font-semibold text-bad">
                {e}
              </p>
            ))}
            {importProspects.error && (
              <p role="alert" className="text-sm font-semibold text-bad">
                Falha ao importar: {importProspects.error.message}
              </p>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStep("source")}>
                Voltar
              </Button>
              <Button
                type="button"
                disabled={!hasName || limitErrors.length > 0 || importProspects.isPending}
                onClick={runImport}
              >
                {importProspects.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Importar {sheet.records.length} linhas
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "done" && result && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              <StatusBadge tone="ok">{result.inserted} novos</StatusBadge>
              <StatusBadge tone="info">{result.updated} atualizados</StatusBadge>
              {result.skipped > 0 && (
                <StatusBadge tone="neutral">{result.skipped} já existiam</StatusBadge>
              )}
              {result.errors.length > 0 && (
                <StatusBadge tone="warn">{result.errors.length} linhas com aviso</StatusBadge>
              )}
            </div>
            {result.createdFields.length > 0 && (
              <p className="text-sm">Colunas criadas: {result.createdFields.join(", ")}.</p>
            )}
            {result.errors.length > 0 && (
              <ul className="max-h-[220px] overflow-y-auto rounded-xl border border-border p-3 text-[12.5px]">
                {result.errors.slice(0, 50).map((e) => (
                  <li key={e.row} className="py-0.5">
                    <strong>Linha {e.row + 1} da planilha:</strong> {e.details.join("; ")}
                  </li>
                ))}
              </ul>
            )}
            <DialogFooter>
              <Button type="button" onClick={() => close(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
